/**
 * ============================================================
 * KEJJA OS — src/pages/notifications/NotificationsPage.tsx
 * Full list + filter tabs + preferences tab.
 * ============================================================
 */

import { useState } from 'react';
import { NavLink, Outlet, Routes, Route, useNavigate } from 'react-router-dom';
import {
  Bell, CheckCheck, Trash2, X, Send, Sliders,
} from 'lucide-react';
import {
  useNotifications, useMarkAllRead, useDismissAll, useDismissNotification, useMarkRead,
  useTestNotification,
} from '@/hooks/useNotifications';
import type { Notification, ListFilter } from '@/lib/notificationsApi';

import NotificationPreferencesPage from './NotificationPreferencesPage';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export default function NotificationsPage() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<ListView />} />
        <Route path="preferences" element={<NotificationPreferencesPage />} />
      </Route>
    </Routes>
  );
}

function Layout() {
  return (
    <div className="space-y-5">
      <header className="border-b border-border pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 flex items-center justify-center flex-shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold leading-tight tracking-tight">Notifications</h1>
            <p className="text-xs text-muted-foreground mt-0.5 italic">Don't miss what matters.</p>
          </div>
        </div>
      </header>

      <nav className="flex items-center gap-1 overflow-x-auto border-b border-border">
        <TabLink to="/notifications" end icon={Bell} label="All" />
        <TabLink to="/notifications/preferences" icon={Sliders} label="Preferences" />
      </nav>

      <Outlet />
    </div>
  );
}

function TabLink({ to, end, icon: Icon, label }: {
  to: string; end?: boolean; icon: React.ComponentType<{ className?: string }>; label: string;
}) {
  return (
    <NavLink
      to={to} end={end}
      className={({ isActive }) => cn(
        'flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors',
        isActive
          ? 'border-amber-500 text-amber-700 dark:text-amber-300'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </NavLink>
  );
}

// ============================================================
// List view
// ============================================================

const FILTERS: Array<{ value: ListFilter; label: string }> = [
  { value: 'all',       label: 'All' },
  { value: 'unread',    label: 'Unread' },
  { value: 'read',      label: 'Read' },
  { value: 'dismissed', label: 'Dismissed' },
];

function ListView() {
  const [filter, setFilter] = useState<ListFilter>('all');
  const { data, isLoading } = useNotifications(filter);
  const markAllMut = useMarkAllRead();
  const dismissAllMut = useDismissAll();
  const testMut = useTestNotification();

  const items = data?.notifications ?? [];

  return (
    <div className="space-y-4 pb-16">
      <div className="flex items-center gap-1.5 overflow-x-auto">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'flex-shrink-0 px-3 py-1 rounded-full text-[11px] font-medium border transition-colors',
              filter === f.value
                ? 'border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                : 'border-border text-muted-foreground hover:bg-muted',
            )}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <Button
          variant="outline" size="sm"
          className="text-xs"
          onClick={() => testMut.mutate()}
          disabled={testMut.isPending}
        >
          <Send className="w-3 h-3 mr-1" />
          {testMut.isPending ? 'Sending…' : 'Test'}
        </Button>
      </div>

      {filter !== 'dismissed' && items.length > 0 && (
        <div className="flex gap-1.5">
          <Button
            size="sm" variant="ghost" className="text-xs"
            onClick={() => markAllMut.mutate()}
          >
            <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
          </Button>
          <Button
            size="sm" variant="ghost" className="text-xs text-muted-foreground"
            onClick={() => {
              if (confirm('Dismiss all notifications? This moves them out of your main list but they stay in the Dismissed view.')) {
                dismissAllMut.mutate();
              }
            }}
          >
            <Trash2 className="w-3 h-3 mr-1" /> Dismiss all
          </Button>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-48" />
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Bell className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {filter === 'unread' ? 'No unread notifications.'
                : filter === 'dismissed' ? 'Nothing dismissed.'
                : "You're all caught up."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {items.map((n) => <FullRow key={n.id} notification={n} />)}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// Full list row (larger than the dropdown row)
// ============================================================

function FullRow({ notification }: { notification: Notification }) {
  const markReadMut = useMarkRead();
  const dismissMut = useDismissNotification();
  const navigate = useNavigate();

  const isUnread = notification.state === 'delivered' || notification.state === 'pending';
  const isDismissed = notification.state === 'dismissed';

  const handleClick = () => {
    if (isUnread) markReadMut.mutate(notification.id);
    if (notification.actionUrl) navigate(notification.actionUrl);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'p-4 flex items-start gap-3 cursor-pointer hover:bg-muted/30 transition-colors',
        isUnread && 'bg-amber-50/50 dark:bg-amber-950/10',
        isDismissed && 'opacity-60',
      )}
    >
      <div className={cn(
        'h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 text-lg',
        notification.priority === 'critical'
          ? 'bg-rose-100 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
          : notification.priority === 'quiet'
          ? 'bg-muted text-muted-foreground'
          : 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
      )}>
        {notification.icon ?? '🔔'}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <span className={cn(
            'text-sm flex-1',
            isUnread ? 'font-semibold' : 'text-muted-foreground',
          )}>{notification.title}</span>
          <Badge variant="outline" className="text-[9px] h-4 px-1 capitalize">
            {notification.module}
          </Badge>
          {notification.priority === 'critical' && (
            <Badge variant="outline" className="text-[9px] h-4 px-1 border-rose-500/60 text-rose-700 dark:text-rose-300">
              critical
            </Badge>
          )}
        </div>

        {notification.body && (
          <p className="text-xs text-muted-foreground mt-1">{notification.body}</p>
        )}

        <div className="flex items-center gap-3 mt-2">
          <span className="text-[10px] text-muted-foreground">
            {new Date(notification.createdAt).toLocaleString('en-KE', {
              month: 'short', day: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </span>
          {notification.actionUrl && notification.actionLabel && (
            <span className="text-[10px] text-amber-700 dark:text-amber-300 font-semibold">
              → {notification.actionLabel}
            </span>
          )}
        </div>
      </div>

      {!isDismissed && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismissMut.mutate(notification.id);
          }}
          className="flex-shrink-0 h-7 w-7 rounded-md hover:bg-muted text-muted-foreground flex items-center justify-center"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
